import React, { useState } from "react";
import Select, { SelectOption } from "../../../componentLibrary/Select";
import Combobox, { ComboboxOptionI } from "../../../componentLibrary/Combobox";
import Input from "../../../componentLibrary/Input";
import Textarea from "../../../componentLibrary/Textarea";
import RadioGroup, {
  RadioGroupOption,
} from "../../../componentLibrary/RadioGroup";
import MultiSelectDropdown, {
  MultiSelectOptionI,
} from "../../../componentLibrary/MultiSelectDropdown";
import Checkbox from "../../../componentLibrary/Checkbox";
import CheckboxGroup from "../../../componentLibrary/CheckboxGroup";
import Checkboxes from "../../../componentLibrary/Checkboxes";
import FilterChipGroup from "../../../componentLibrary/FilterChipGroup";

export default function FormComponents() {
  const [selectedCheckbox, setSelectedCheckbox] = useState([]);
  const [selectedFilterChip, setSelectedFilterChip] = useState<string | null>(
    null,
  );
  const [selectedOption, setSelectionOption] = useState<RadioGroupOption>({
    value: "",
    label: "",
  });
  const [selectVal, setSelectVal] = useState<SelectOption>({
    value: "option1",
    label: "Option 1",
  });
  const [comboVal, setComboVal] = useState<ComboboxOptionI>({
    value: "",
    label: "",
  });
  const [multiVal, setMultiVal] = useState<MultiSelectOptionI[]>([]);

  return (
    <>
      <h2 className="underline">Form Components</h2>

      <h3 className="underline">Select Components</h3>
      <div className="mb-6">
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Usage:</h4>
          <p className="text-sm text-gray-700">
            Dropdown selection components used throughout the application for
            data selection, filtering, and user input. Includes standard select
            dropdowns, searchable comboboxes, and multi-select options for
            complex data entry scenarios.
          </p>
        </div>
        <Select
          label="Dropdown label"
          options={[
            { value: "option1", label: "Option 1" },
            { value: "option2", label: "Option 2" },
          ]}
          value={selectVal}
          onChange={setSelectVal}
        />
      </div>
      <div className="space-y-2 mb-4">
        <Combobox
          label="Combobox label"
          options={[
            { value: "option1", label: "Jim" },
            { value: "option2", label: "Sue" },
            { value: "option3", label: "Bill" },
          ]}
          value={comboVal}
          onChange={(option) => setComboVal(option)}
        />
        <MultiSelectDropdown
          label="Multi-select"
          options={[
            { value: "option1", label: "Jim" },
            { value: "option2", label: "Sue" },
            { value: "option3", label: "Bill" },
          ]}
          value={multiVal}
          onChange={(option) => setMultiVal(option)}
        />
      </div>
      <h3 className="underline">Input Components</h3>
      <div className="mb-4 p-4 bg-gray-50 rounded-lg">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Usage:</h4>
        <p className="text-sm text-gray-700">
          Text input components for user data entry. Used in authentication
          forms, content creation, user profile management, and various data
          entry scenarios throughout the application. Includes single-line
          inputs and multi-line textareas.
        </p>
      </div>
      <Input label="This is the input label" />
      <Input label="This is a required input" required />
      <Textarea label="Textarea label" rows={5} />
      <h3 className="underline">Checkbox Selection</h3>
      <div className="mb-4 p-4 bg-gray-50 rounded-lg">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Usage:</h4>
        <p className="text-sm text-gray-700">
          Selection components for boolean and multiple choice inputs. Used in
          lesson responses, user preferences, filtering options, and various
          forms throughout the application. Includes individual checkboxes,
          radio groups, and grouped checkbox components.
        </p>
      </div>

      <h4 className="text-lg font-semibold mb-3">Individual Checkbox</h4>
      <div className="mb-4 p-4 bg-gray-50 rounded-lg">
        <h5 className="text-sm font-medium text-gray-900 mb-2">Usage:</h5>
        <p className="text-sm text-gray-700">
          Standard checkbox for boolean inputs. Used for toggles, settings, and
          simple yes/no questions. Common in forms for enabling/disabling
          features, accepting terms, and simple binary choices.
        </p>
      </div>
      <Checkbox label="Tell me what it was like for you when this situation happened." />
      <Checkbox label="Single Line" />
      <h3 className="mt-6 mb-2 text-lg font-semibold">
        Is this habit linked to a goal that is still a priority?
      </h3>
      <Checkbox label="Yes" />
      <Checkbox label="No" />
      <h3 className="mt-6 mb-2 text-lg font-semibold">Did you:</h3>
      <Checkbox label="Start by changing your behavior in small steps?" />
      <Checkbox label="Were you overly ambitious in trying for a big change?" />

      <h4 className="text-lg font-semibold mb-3 mt-6">Radio Group</h4>
      <div className="mb-4 p-4 bg-gray-50 rounded-lg">
        <h5 className="text-sm font-medium text-gray-900 mb-2">Usage:</h5>
        <p className="text-sm text-gray-700">
          Single selection from multiple options. Used for mutually exclusive
          choices like emotion selection and preference settings. Each option is
          visually distinct and only one can be selected.
        </p>
      </div>
      <RadioGroup
        options={[
          { label: "Sad", value: "sad" },
          { label: "Confused", value: "confused" },
          { label: "Yearning", value: "yearning" },
          { label: "Numb", value: "numb" },
        ]}
        selectedValue={selectedOption.value}
        onChange={setSelectionOption}
      />

      <h4 className="text-lg font-semibold mb-3 mt-6">
        Checkboxes (Multi-select)
      </h4>
      <div className="mb-4 p-4 bg-gray-50 rounded-lg">
        <h5 className="text-sm font-medium text-gray-900 mb-2">Usage:</h5>
        <p className="text-sm text-gray-700">
          Multiple selection with enhanced visual feedback. Used for filtering,
          tagging, and selecting multiple options from a list. Features hover
          states and clear visual indication of selected items. Common in filter
          modals and lesson response components.
        </p>
      </div>
      <Checkboxes
        options={["Reflection", "Activity", "Section Recap"]}
        selectedOptions={selectedCheckbox}
        setSelectedOptions={setSelectedCheckbox}
      />

      <h4 className="text-lg font-semibold mb-3 mt-6">
        Checkbox Group (Button-style)
      </h4>
      <div className="mb-4 p-4 bg-gray-50 rounded-lg">
        <h5 className="text-sm font-medium text-gray-900 mb-2">Usage:</h5>
        <p className="text-sm text-gray-700">
          Button-style multi-select for category filtering and navigation. Used
          in module lists and content filtering where options are displayed as
          interactive buttons. Provides a compact, grid-based layout for
          multiple selection scenarios.
        </p>
      </div>
      <div className="flex space-x-2">
        <CheckboxGroup
          options={["Option 1", "Option 2", "Option 3"]}
          selectedOptions={selectedCheckbox}
          setSelectedOptions={setSelectedCheckbox}
        />
      </div>

      <h4 className="text-lg font-semibold mb-3 mt-6">
        Filter Chip Group (Button-style, single-select)
      </h4>
      <div className="mb-4 p-4 bg-gray-50 rounded-lg">
        <h5 className="text-sm font-medium text-gray-900 mb-2">Usage:</h5>
        <p className="text-sm text-gray-700">
          Single-select pill filter for mutually exclusive options (e.g.
          category filters). Clicking the active chip deselects it and returns{" "}
          <code>null</code>. Use <strong>CheckboxGroup</strong> above when
          multiple selections are needed.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <FilterChipGroup
          options={["Option 1", "Option 2", "Option 3"]}
          selectedOption={selectedFilterChip}
          onChange={setSelectedFilterChip}
        />
      </div>
      <p className="mt-3 text-sm text-gray-500">
        Selected: <strong>{selectedFilterChip ?? "none"}</strong>
      </p>
    </>
  );
}
